{
  readAscii
  readInt24
  readVarInt
} from ./file.civet

toSigned := (x: number) ->
  if x > 127
    x - 256
  else
    x

fromSigned := (x: number) ->
  if x < 0
    x + 256
  else
    x

writeInt24 := (state: {data: Uint8Array, position: number}, v: number) ->
  {data, position} := state
  state.position = position + 3

  data[position + 0] = (v >> 16) & 0xff
  data[position + 1] = (v >>  8) & 0xff
  data[position + 2] =  v        & 0xff

  return 3

state :=
  data: new Uint8Array
  position: 0

/**
 * An event in a MIDI file with tick postion included.
 *
 * Resources:
 * - https://www.music.mcgill.ca/~ich/classes/mumt306/StandardMIDIfileformat.html
 */
export class MidiEvent
  /** absolute tick position, not delta time */
  tick: number
  data: Uint8Array<ArrayBuffer>
  @(@tick: number, @data: Uint8Array<ArrayBuffer>)

  /** Create a new MidiEvent from a tick position and data. */
  copy()
    return new MidiEvent(@tick, @data.slice())

  /** Channel number for non-meta events */
  get channel(): number
    if !@meta
      @typeByte & 0x0f
    else
      0
  set channel(c: number)
    if !@meta
      @typeByte = (@typeByte & 0xf0) | (c & 0x0f)

  /** Is this a meta event? */
  get meta
    @typeByte is 0xff

  /** shorthand for unchecked channel number, data[0] & 0x0f */
  get c(): number
    @data[0] & 0x0f

  /** shorthand for unchecked note number, data[1] */
  get n(): number
    @data[1]

  /** shorthand for unchecked velocity, data[2] */
  get v(): number
    @data[2]

  /** 14-bit pitch bend value: LSB in data[1], MSB in data[2], center 8192 */
  get bend(): number
    (@data[2] << 7) | @data[1]

  /** note number for noteOn/noteOff events */
  get note(): number?
    b := @typeByte >> 4

    if b is 0x8 or b is 0x9
      @data[1]
  set note(v: number)
    b := @typeByte >> 4

    if b is 0x8 or b is 0x9
      @data[1] = v

  /** numerator for timeSignature event */
  get numerator(): number
    if @meta and @data[1] is 0x58
      @data[3]
    else
      4
  set numerator(v: number)
    if @meta and @data[1] is 0x58
      @data[3] = v

  /** denominator for timeSignature event */
  get denominator(): number
    if @meta and @data[1] is 0x58
      Math.pow 2, @data[4]
    else
      4

  set denominator(v: number)
    if @meta and @data[1] is 0x58
      @data[4] = Math.log2 v

  /** MIDI clocks per metronome click for timeSignature event */
  get metronome(): number
    if @meta and @data[1] is 0x58
      @data[5]
    else
      24

  set metronome(v: number)
    if @meta and @data[1] is 0x58
      @data[5] = v

  /** 32nd notes per MIDI quarter note for timeSignature event */
  get thirtySeconds(): number
    if @meta and @data[1] is 0x58
      @data[6]
    else
      8

  set thirtySeconds(v: number)
    if @meta and @data[1] is 0x58
      @data[6] = v

  /**
   * key for keySignature event
   * -7 to 7 negative is flats, positive is sharps
   */
  get key(): number
    if @meta and @data[1] is 0x59
      toSigned @data[3]
    else
      0
  set key(v: number)
    if @meta and @data[1] is 0x59
      @data[3] = fromSigned v

  /**
   * scale for keySignature event
   * 0 for major, 1 for minor
   */
  get scale(): number
    if @meta and @data[1] is 0x59
      @data[4]
    else
      0
  set scale(v: number)
    if @meta and @data[1] is 0x59
      @data[4] = v

  /** program value of programChange event */
  get program(): number
    b := @typeByte >> 4

    if b is 0xc
      @data[1]
    else
      0
  set program(v: number)
    b := @typeByte >> 4

    if b is 0xc
      @data[1] = v

  /** @deprecated */
  get tempo(): number
    return @microsecondsPerBeat

  /** microseconds per beat for setTempo event */
  get microsecondsPerBeat(): number
    {data} := @
    if @typeByte is 0xff and data[1] is 0x51
      state.data = data
      state.position = 3
      readInt24(state)
    else
      0

  set microsecondsPerBeat(v: number)
    {data} := @
    if @typeByte is 0xff and data[1] is 0x51
      state.data = data
      state.position = 3
      writeInt24 state, v

  /** cached ascii text */
  _text: string?
  /** Text for text, copyrightNotice, lyrics, etc. events */
  get text
    text := @_text
    if text?
      return text
    else
      b := @subtypeByte
      if @meta and 0x1 <= b <= 0x7
        state.data = @data
        state.position = 2 // skipping type byte, subtype byte
        length := readVarInt(state)
        return @_text = readAscii(state, length)
    return ""

  /** type of event as a string name */
  get type
    e := @typeByte
    // mask out channel
    c := e & 0x0f

    // https://www.midi.org/specifications-old/item/table-1-summary-of-midi-message
    switch e >> 4
      when 0x8
        'noteOff'
      when 0x9
        if @data[2] is 0
          'noteOff'
        else
          'noteOn'
      when 0xa // Polyphonic Key Pressure (Aftertouch)
        'keyPressure'
      when 0xb
        n := @data[1]
        if n < 120
          'controlChange'
        else
          switch n
            when 0x78
              'allSoundsOff'
            when 0x79
              'resetControllers'
            when 0x7a
              ; // TODO local control on/off
            when 0x7b, 0x7c, 0x7d, 0x7e, 0x7f
              // All notes off; 0x7c–0x7f are also mode changes, which ChannelData
              // forwards as the controller.
              'allNotesOff'

      when 0xc
        'programChange'
      when 0xd
        'channelPressure'
      when 0xe
        'pitchBend'
      when 0xf // System Event
        switch c
          when 0x0 // sysex
            'sysEx'
          when 0x1 // MIDI Time Code Quarter Frame
            ; // TODO
          when 0x2 // Song Position Pointer
            ; // TODO
          when 0x3 // Song Select
            ; // TODO
          when 0x6 // Tune Request
            ; // TODO
          when 0x7 // end sysex
            ; // TODO
          when 0xf // meta
            n := @data[1]
            switch n
              when 0x00
                'sequenceNumber'
              when 0x01
                'text'
              when 0x02
                'copyrightNotice'
              when 0x03
                'trackName'
              when 0x04
                'instrumentName'
              when 0x05
                'lyrics'
              when 0x06
                'marker'
              when 0x07
                'cuePoint'
              when 0x20
                'channelPrefix'
              when 0x2f
                'endOfTrack'
              when 0x51
                'setTempo'
              when 0x54
                'smpteOffset'
              when 0x58
                'timeSignature'
              when 0x59
                'keySignature'
              when 0x7f
                'sequencerSpecific'
              else
                'unknown'

  get typeByte
    @data[0]
  set typeByte(byte)
    @data[0] = byte

  get subtypeByte
    @data[1]
  set subtypeByte(byte)
    @data[1] = byte

  /** velocity for noteOn/noteOff event scaled to [0.0 - 1.0] */
  get velocity
    b := @typeByte >> 4
    let v

    if b is 0x8
      v = @data[2]
    else if b is 0x9
      v = @data[2]
      if v is 0
        v = 0x40

    if v?
      v / 0x7f
    else
      0.5

export default MidiEvent

/*
For adding smpteOffset event later

  assert length is 5
  hourByte := readInt8(state)
  frameRate := frameRateTable[hourByte & 0x60]
  hour := hourByte & 0x1f
  min := readInt8(state)
  sec := readInt8(state)
  frame := readInt8(state)
  subframe := readInt8(state)
  smpteOffset?(frameRate, hour, min, sec, frame, subframe)


frameRateTable := {
  0x00: 24
  0x20: 25
  0x40: 29
  0x60: 30
} as Record<number, 24 | 25 | 29 | 30>

*/
